import { Component } from "@theme/component";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { fetchConfig } from "@theme/utilities";
import { morphSection } from "@theme/section-renderer";

/**
 * Gift wrap opt-in on the product page.
 *
 * The checkbox only stores intent; when the product form in the same section
 * successfully adds the product, the gift-wrap variant is nested under the
 * freshly added cart line via Shopify's Nested Cart Lines API, so the cart's
 * existing per-line gift wrap checkbox (cart-line-gift-wrap.liquid) picks it
 * up and unchecking in the cart works through cart-items.js unchanged.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} [checkbox]
 *
 * @extends {Component<Refs>}
 */
class ProductGiftWrap extends Component {
  /** @type {AbortController | undefined} */
  #abort;
  #busy = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    // CartAddEvent bubbles from the product-form-component in this section.
    // The sticky add-to-cart bar submits the same form, so it is covered too.
    this.closest(".shopify-section, dialog")?.addEventListener(ThemeEvents.cartUpdate, this.#onCartAdd, {
      signal: this.#abort.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = undefined;
  }

  get #giftVariantId() {
    return Number(this.dataset.giftVariantId) || 0;
  }

  get #syncQuantity() {
    return this.dataset.giftSyncQuantity === "true";
  }

  /**
   * Reacts only to a successful add from this product's own form. Our own
   * mutations dispatch CartUpdateEvent (source "product-gift-wrap") on
   * cart-items components outside this section, so they never re-trigger.
   *
   * @type {EventListener}
   */
  #onCartAdd = (event) => {
    const detail = /** @type {{ detail?: { resource?: object, data?: Record<string, unknown> } }} */ (event).detail;
    const data = detail?.data;

    if (this.#busy || !this.#giftVariantId) return;
    if (this.refs.checkbox?.checked !== true) return;
    if (data?.source !== "product-form-component" || data?.isIncremental !== true) return;
    if (String(data.productId) !== String(this.dataset.productId)) return;

    this.#attachGift(detail?.resource, data.variantId);
  };

  /**
   * @param {object | undefined} addResponse - The raw /cart/add.js response.
   * @param {unknown} addedVariantId
   */
  async #attachGift(addResponse, addedVariantId) {
    this.#busy = true;
    this.#setCartLoading(true);

    try {
      const added = /** @type {{ key?: string, items?: Array<{ key?: string }> }} */ (addResponse)?.items?.[0] ??
        /** @type {{ key?: string }} */ (addResponse) ?? {};
      const cart = await this.#fetchCart();

      // Prefer the key from the add response (also correct when the add merged
      // into an existing line); fall back to the newest top-level line of the
      // added variant.
      const parent =
        cart.items.find((i) => added.key && i.key === added.key) ??
        cart.items.find(
          (i) => Number(i.variant_id) === Number(addedVariantId) && !i.parent_relationship?.parent_key
        );
      if (!parent) return;

      const giftQuantity = this.#syncQuantity ? parent.quantity || 1 : 1;

      // Line already gift wrapped (e.g. same product added twice): sync qty.
      const child = cart.items.find(
        (i) => Number(i.variant_id) === this.#giftVariantId && i.parent_relationship?.parent_key === parent.key
      );
      if (child) {
        if (child.quantity !== giftQuantity) {
          const res = await fetch(
            FoxTheme.routes.cart_change_url,
            fetchConfig("json", {
              body: JSON.stringify({ id: child.key, quantity: giftQuantity, ...this.#getSectionPayload() }),
            })
          );
          await this.#applyCartJsonResponse(await res.json());
        }
        return;
      }

      // Remove the parent, then re-add parent + gift child in one request
      // using `parent_id` (mirrors cart-items.js #addPerLineGift; keys of
      // discounted lines are volatile, so `parent_line_key` is unreliable).
      const parentPayload = {
        id: parent.variant_id,
        quantity: parent.quantity,
        properties: { ...(parent.properties || {}) },
      };
      const sellingPlanId = parent.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlanId) parentPayload.selling_plan = sellingPlanId;

      await fetch(
        FoxTheme.routes.cart_change_url,
        fetchConfig("json", {
          body: JSON.stringify({ id: parent.key, quantity: 0 }),
        })
      );

      const res = await fetch(
        FoxTheme.routes.cart_add_url,
        fetchConfig("json", {
          body: JSON.stringify({
            items: [
              parentPayload,
              {
                id: this.#giftVariantId,
                quantity: giftQuantity,
                parent_id: parent.variant_id,
              },
            ],
            ...this.#getSectionPayload(),
          }),
        })
      );
      const parsed = await res.json();

      if (parsed.status && parsed.message && !parsed.items) {
        // A 422 can still have partially mutated the cart, so only re-add the
        // parent when it is really gone — the customer must never lose the
        // product, but must not get a doubled quantity either.
        console.error(parsed.message);
        const cartAfter = await this.#fetchCart();
        const parentRestored = cartAfter.items.some(
          (i) => Number(i.variant_id) === Number(parent.variant_id) && !i.parent_relationship?.parent_key
        );
        if (parentRestored) {
          await this.#refreshCartSections();
        } else {
          const restore = await fetch(
            FoxTheme.routes.cart_add_url,
            fetchConfig("json", {
              body: JSON.stringify({ items: [parentPayload], ...this.#getSectionPayload() }),
            })
          );
          await this.#applyCartJsonResponse(await restore.json());
        }
        return;
      }

      await this.#applyCartJsonResponse(parsed);
    } catch (error) {
      console.error("Gift wrap add failed:", error);
    } finally {
      this.#setCartLoading(false);
      this.#busy = false;
    }
  }

  /**
   * @returns {Promise<{ items: Array<Record<string, any>> }>}
   */
  async #fetchCart() {
    const res = await fetch(FoxTheme.routes.cart);
    return res.json();
  }

  /**
   * Re-fetches and morphs cart sections when a response carried no sections
   * (Shopify omits them on 422 errors).
   */
  async #refreshCartSections() {
    const payload = this.#getSectionPayload();
    const ids = payload.sections ? payload.sections.split(",") : [];
    const sections = {};
    await Promise.all(
      ids.map(async (sectionId) => {
        const res = await fetch(`${window.location.pathname.split("?")[0]}?section_id=${sectionId}`);
        sections[sectionId] = await res.text();
      })
    );
    if (Object.keys(sections).length > 0) {
      await this.#applyCartJsonResponse({ sections });
    }
  }

  #getSectionPayload() {
    const ids = [];
    document.querySelectorAll("cart-items-component").forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return {
      sections: ids.join(","),
      sections_url: window.location.pathname,
    };
  }

  /**
   * @param {{ sections?: Record<string, string> }} parsed
   */
  async #applyCartJsonResponse(parsed) {
    if (!parsed || !parsed.sections) return;

    const sections = parsed.sections;
    let itemCount = 0;

    for (const sectionId of Object.keys(sections)) {
      await morphSection(sectionId, sections[sectionId]);
      const doc = new DOMParser().parseFromString(sections[sectionId], "text/html");
      const countEl = doc.querySelector('[ref="cartItemCount"]');
      if (countEl?.textContent) {
        itemCount = parseInt(countEl.textContent, 10) || itemCount;
      }
    }

    document.querySelectorAll("cart-items-component").forEach((comp) => {
      if (!(comp instanceof HTMLElement)) return;
      const sid = comp.dataset.sectionId;
      if (sid && sections[sid]) {
        comp.dispatchEvent(
          new CartUpdateEvent({}, sid, {
            itemCount,
            source: "product-gift-wrap",
            sections,
          })
        );
      }
    });
  }

  /**
   * @param {boolean} on
   */
  #setCartLoading(on) {
    document.querySelectorAll("cart-items-component").forEach((comp) => {
      comp.classList.toggle("cart-items-disabled", on);
    });
  }
}

if (!customElements.get("product-gift-wrap")) {
  customElements.define("product-gift-wrap", ProductGiftWrap);
}
